/**
 * Wizard chain controller. Given an event + payload, plays each
 * registered hook in order:
 *
 *   parent: <WizardChain
 *             eventKind="org.created"
 *             event={{ org }}
 *             isActive={chainActive}
 *             onAllDone={() => setChainActive(false)}
 *           />
 *
 * Each hook renders itself (`isActive` is true for the current step,
 * false for the rest). When a step finishes it calls `onComplete` or
 * `onSkip`; we bump the index and the next step takes over. After
 * the last step, `onAllDone` fires and the parent can dismiss / clear
 * any state it had around the chain.
 *
 * Inactive hooks still render (with `isActive: false`) so a hook that
 * persists internal state — say, an inline form that's been partially
 * filled out — keeps its state across the chain. Most hooks just
 * render a closed `<Dialog open={false}>` when inactive and don't
 * pay any cost.
 *
 * Reset behaviour: when `isActive` flips false → true, the chain
 * resets to the first hook with empty results. This matches the
 * "start a fresh chain each time" semantics callers expect.
 */
import { useEffect, useState } from 'react';
import { getWizardHooksFor } from './registry.js';
import type { WizardEventKind, WizardEventPayloads } from './types.js';

interface WizardChainProps<K extends WizardEventKind> {
  eventKind: K;
  /**
   * The event payload. Must be set when `isActive` is true. When
   * `isActive` is false the value is ignored (and may be null) —
   * useful for parents that don't have the event yet.
   */
  event: WizardEventPayloads[K] | null;
  /**
   * Whether the chain is currently playing. Flipping false → true
   * resets to step 0 with empty results; flipping true → false
   * leaves the inactive hooks rendered with `isActive: false` (they
   * typically render nothing, but persist internal state for
   * smoother re-open).
   */
  isActive: boolean;
  /** Called after the last hook completes (or skips). */
  onAllDone: () => void;
}

export function WizardChain<K extends WizardEventKind>({
  eventKind,
  event,
  isActive,
  onAllDone,
}: WizardChainProps<K>): JSX.Element | null {
  const hooks = getWizardHooksFor(eventKind);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [results, setResults] = useState<Record<string, unknown>>({});

  // Reset when the chain (re)activates.
  useEffect(() => {
    if (isActive) {
      setCurrentIdx(0);
      setResults({});
    }
  }, [isActive]);

  // No hooks registered → fire onAllDone immediately so the parent
  // doesn't get stuck. Edge case but happens during early bootstrap.
  useEffect(() => {
    if (isActive && hooks.length === 0) {
      onAllDone();
    }
  }, [isActive, hooks.length, onAllDone]);

  // No event payload → nothing to render.
  if (!event) return null;

  function advance(hookId: string, data?: unknown): void {
    if (data !== undefined) {
      setResults((r) => ({ ...r, [hookId]: data }));
    }
    if (currentIdx + 1 >= hooks.length) {
      // Last hook done — defer the onAllDone call so React commits the
      // results update first. setTimeout(..., 0) is enough; we could
      // use a layout effect but the explicit deferral is clearer here.
      setTimeout(() => onAllDone(), 0);
    } else {
      setCurrentIdx((i) => i + 1);
    }
  }

  return (
    <>
      {hooks.map((hook, idx) => {
        const Step = hook.render;
        return (
          <Step
            key={hook.id}
            isActive={isActive && idx === currentIdx}
            event={event}
            results={results}
            onComplete={(data) => advance(hook.id, data)}
            onSkip={() => advance(hook.id)}
          />
        );
      })}
    </>
  );
}
